import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './AdminUpload.css';
import api, { API_BASE } from '../api';
import PressCard from '../components/PressCard';
import IDCardBack from '../components/IDCardBack';
import AdminVideoManager from '../components/AdminVideoManager';
import ConsentForm from './ConsentForm';
import { generateConsentLetterPDF } from '../utils/generateConsentLetter';
import logo from '../images/logo-rr-news.png';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
// PressCard injects its styles at runtime; no separate CSS file needed

function AdminUpload() {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [author, setAuthor] = useState('');
  const [location, setLocation] = useState('');
  const [type, setType] = useState('normal'); // normal | gallery | event
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviewUrls, setGalleryPreviewUrls] = useState([]);
  const [eventDate, setEventDate] = useState('');
  const [eventVenue, setEventVenue] = useState('');
  const [image, setImage] = useState(null);
  const [fileName, setFileName] = useState('No file chosen');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [video, setVideo] = useState(null);
  const [videoFileName, setVideoFileName] = useState('No file chosen');
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const previewIsObject = useRef(false);
  const videoPreviewIsObject = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const navLocation = useLocation();
  const editState = navLocation.state && navLocation.state.edit;
  const editNews = navLocation.state && navLocation.state.news;

  const token = localStorage.getItem('token');
  const storedRole = localStorage.getItem('role');
  const [showCard, setShowCard] = useState(false);
  const [showConsentForm, setShowConsentForm] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [cardPhotoDataUrl, setCardPhotoDataUrl] = useState(null);
  const [consentFormData, setConsentFormData] = useState(null);
  const [generatingLetterPDF, setGeneratingLetterPDF] = useState(false);
  const [letterDownloadSuccess, setLetterDownloadSuccess] = useState(null);
  const cardRef = React.useRef(null);
  
  const applyCaptureStyles = () => {
    if (typeof document === 'undefined') return;
    const id = 'capture-override';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.appendChild(document.createTextNode(`
      .idcard-frame, .idcard-inner, .blue-strip, .strip-text, .email-strip, .press-card { font-family: "Noto Sans", Arial, sans-serif !important; -webkit-font-smoothing: antialiased; box-sizing: border-box !important; }
      .blue-strip { display: flex !important; align-items: center !important; min-height: 20px !important; padding: 0 8px !important; }
      .email-strip { display: flex !important; flex-direction: column !important; gap: 4px !important; padding: 6px 8px !important; }
    `));
    document.head.appendChild(s);
  };
  const removeCaptureStyles = () => { const id='capture-override'; const el=document.getElementById(id); if(el) el.remove(); };
  // Clone-capture helper used by modal capture flows. Returns { canvas, cssW, cssH }.
  const captureCloneAtWidth = async (sourceEl, forcedWidth) => {
    if (!sourceEl) throw new Error('No element to capture');
    const targetWidth = forcedWidth || Math.round((sourceEl.getBoundingClientRect && sourceEl.getBoundingClientRect().width) || 380);
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '0';
    wrapper.style.top = '0';
    wrapper.style.width = targetWidth + 'px';
    wrapper.style.height = 'auto';
    wrapper.style.overflow = 'visible';
    wrapper.style.background = 'white';
    wrapper.style.opacity = '0';
    wrapper.style.pointerEvents = 'none';
    wrapper.style.userSelect = 'none';
    wrapper.style.zIndex = '999999';
    const clone = sourceEl.cloneNode(true);
    clone.querySelectorAll && clone.querySelectorAll('[id]').forEach(n => n.removeAttribute('id'));
    clone.style.width = targetWidth + 'px';
    clone.style.maxWidth = targetWidth + 'px';
    clone.style.boxSizing = 'border-box';
    clone.style.display = 'block';
    clone.style.maxHeight = 'none';
    clone.style.transform = 'none';
    try {
      const fixedEls = clone.querySelectorAll('*');
      fixedEls.forEach((el) => {
        try {
          const cs = window.getComputedStyle(el);
          if (cs && cs.position === 'fixed') el.style.position = 'relative';
        } catch (e) {}
      });
    } catch (e) {}
    clone.querySelectorAll && clone.querySelectorAll('img').forEach(img => { img.crossOrigin = 'anonymous'; img.style.maxWidth = '100%'; });
    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);
    const imgs = Array.from(wrapper.querySelectorAll('img'));
    await Promise.all(imgs.map(i => (i.complete ? Promise.resolve() : new Promise(r => { i.onload = i.onerror = r; }))));
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch (e) {}
    }
    const waitForStableSize = async (node, stableMs = 600, timeoutMs = 6000) => {
      let lastRect = node.getBoundingClientRect();
      let stableFor = 0;
      const start = Date.now();
      const interval = 150;
      while (Date.now() - start < timeoutMs) {
        await new Promise(r => setTimeout(r, interval));
        const cur = node.getBoundingClientRect();
        if (Math.abs(cur.width - lastRect.width) < 1 && Math.abs(cur.height - lastRect.height) < 1) {
          stableFor += interval;
          if (stableFor >= stableMs) return true;
        } else {
          stableFor = 0;
          lastRect = cur;
        }
      }
      return false;
    };
    let stable = false;
    try { stable = await waitForStableSize(clone, 800, 8000); } catch (e) { stable = false; }
    const rect = clone.getBoundingClientRect();
    const cssW = Math.round(rect.width);
    const cssH = Math.round(rect.height);
    const DESKTOP_VIEWPORT_WIDTH = 1024;
    const scale = 1;
    const expectedW = Math.round(cssW * scale);
    const expectedH = Math.round(cssH * scale);
    const renderWindowWidth = Math.max(DESKTOP_VIEWPORT_WIDTH, cssW);
    const renderWindowHeight = Math.round(Math.max(expectedH, window.innerHeight || expectedH));
    let canvas = await html2canvas(clone, { scale, useCORS: true, allowTaint: false, backgroundColor: '#ffffff', width: expectedW, height: expectedH, windowWidth: renderWindowWidth, windowHeight: renderWindowHeight });
    if (!canvas) throw new Error('html2canvas failed');
    if (canvas.width !== expectedW || canvas.height !== expectedH) {
      try {
        const fixed = document.createElement('canvas');
        fixed.width = expectedW;
        fixed.height = expectedH;
        const ctx = fixed.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0,0,fixed.width,fixed.height);
        ctx.drawImage(canvas, 0, 0, fixed.width, fixed.height);
        canvas = fixed;
      } catch (e) {}
    }
    try { console.debug('[AdminUpload] captureCloneAtWidth', { targetWidth, cssW, cssH, stable, canvasW: canvas.width, canvasH: canvas.height }); } catch (e) {}
    document.body.removeChild(wrapper);
    return { canvas, cssW, cssH };
  };
  // Inject responsive styles for the modal preview so cards and buttons stack on small screens
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const id = 'adminupload-mobile-styles';
    if (document.getElementById(id)) return;
    const css = `
      @media (max-width: 600px) {
        .admin-card-row { display: block !important; }
        .admincard-col { width: 100% !important; max-width: 100% !important; margin-bottom: 12px !important; }
        .press-modal-actions { display: flex !important; flex-direction: column !important; gap: 8px !important; }
        .press-modal-actions button { width: 100% !important; margin-right: 0 !important; }
      }
    `;
    const s = document.createElement('style'); s.id = id; s.appendChild(document.createTextNode(css)); document.head.appendChild(s);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);
  useEffect(() => {
    // Redirect to login if not authenticated
    if (!token) navigate('/admin/login');
    // If navigated here to edit, prefill the form
    if (editState && editNews) {
      setTitle(editNews.title || '');
      setDescription(editNews.description || '');
      setContent(editNews.content || '');
      setAuthor(editNews.author || '');
      setLocation(editNews.location || '');
      setFileName(editNews.imageUrl ? editNews.imageUrl.split('/').pop() : 'No file chosen');
      setPreviewUrl(editNews.imageUrl || null);
      previewIsObject.current = false;
      setVideoFileName(editNews.videoUrl ? editNews.videoUrl.split('/').pop() : 'No file chosen');
      setVideoPreviewUrl(editNews.videoUrl || null);
      videoPreviewIsObject.current = false;
      setType(editNews.isEvent ? 'event' : (editNews.isGallery ? 'gallery' : 'normal'));
      setEventDate(editNews.eventDate ? new Date(editNews.eventDate).toISOString().slice(0,16) : '');
      setEventVenue(editNews.eventVenue || '');
      // If editing a gallery item, populate gallery preview URLs from saved paths
      if (editNews.isGallery && Array.isArray(editNews.galleryImages) && editNews.galleryImages.length > 0) {
        const urls = editNews.galleryImages.map((p) => {
          if (!p) return null;
          if (/^https?:\/\//i.test(p)) return p;
          const base = API_BASE || '';
          const pref = p.startsWith('/') ? p : '/' + p;
          return (base ? base.replace(/\/$/, '') : '') + pref;
        }).filter(Boolean);
        setGalleryPreviewUrls(urls);
      }
      // note: we don't set image File object here; user can replace it
    }
  }, [token, navigate, editState, editNews]);

  useEffect(() => {
    if (!token) return;
    
    // Clear any stale localStorage data from previous users
    localStorage.removeItem('consentFormData');
    
    const headers = { Authorization: 'Bearer ' + token };
    const loadConsentData = async () => {
      try {
        const res = await api.get('/api/users/me', { headers });
        if (res && res.data && res.data.success) {
          const profile = res.data.data;
          if (!storedRole && profile?.role) {
            try { localStorage.setItem('role', profile.role); } catch (e) {}
          }
          // Only load consent data if user has actually submitted the form
          if (profile?.isConsent === true && profile?.consentData) {
            const mergedConsent = {
              ...profile.consentData,
              name: profile.consentData?.name || profile?.name || ''
            };
            setConsentFormData(mergedConsent);
            console.log('[Consent] loaded consentData from profile');
          } else {
            // Clear consent form data if user hasn't submitted
            setConsentFormData(null);
            console.log('[Consent] user has not submitted consent form');
          }
        }
      } catch (e) {
        console.error('[Consent] failed to load profile consentData:', e);
        setConsentFormData(null);
      }
    };
    loadConsentData();
  }, [storedRole, token]);

  

  // When switching to gallery type, clear single-image / video state to avoid showing them
  useEffect(() => {
    if (type === 'gallery') {
      // clear single image preview/state
      setImage(null);
      setFileName('No file chosen');
      if (previewIsObject.current && previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (e) {}
      }
      setPreviewUrl(null);
      previewIsObject.current = false;

      // clear video preview/state
      setVideo(null);
      setVideoFileName('No file chosen');
      if (videoPreviewIsObject.current && videoPreviewUrl) {
        try { URL.revokeObjectURL(videoPreviewUrl); } catch (e) {}
      }
      setVideoPreviewUrl(null);
      videoPreviewIsObject.current = false;
    }
  }, [type]);

  // cleanup object URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewIsObject.current && previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (e) {}
      }
      if (videoPreviewIsObject.current && videoPreviewUrl) {
        try { URL.revokeObjectURL(videoPreviewUrl); } catch (e) {}
      }
      if (galleryPreviewUrls && galleryPreviewUrls.length) {
        try { galleryPreviewUrls.forEach(u=>URL.revokeObjectURL(u)); } catch(e){}
      }
    };
  }, [previewUrl, videoPreviewUrl]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    // optional: remove other admin-specific state
    localStorage.removeItem('role');
    navigate('/');
  };

  const handleDownloadLetter = async () => {
    if (!consentFormData) {
      alert('कृपया पहले सहमति फॉर्म भरें');
      setShowConsentForm(true);
      return;
    }

    // Double-check with backend that user has submitted consent form
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const checkRes = await api.get('/api/users/me', { headers });
      if (!checkRes?.data?.data?.isConsent) {
        alert('कृपया पहले सहमति फॉर्म भरें');
        setShowConsentForm(true);
        setConsentFormData(null);
        return;
      }
    } catch (e) {
      console.error('Failed to verify consent status:', e);
      alert('कृपया पहले सहमति फॉर्म भरें');
      return;
    }

    const resolvedName = consentFormData.name || myProfile?.name;
    if (!resolvedName || !consentFormData.fatherName || !consentFormData.mobileNumber || !consentFormData.email || !consentFormData.address) {
      alert('कृपया सभी आवश्यक जानकारी भरें');
      return;
    }

    setGeneratingLetterPDF(true);
    try {
      // Fetch latest profile to get current role and ID
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      let latestProfile = myProfile;
      
      try {
        const profileRes = await api.get('/api/users/me', { headers });
        if (profileRes?.data?.data) {
          latestProfile = profileRes.data.data;
        }
      } catch (e) {
        console.warn('Failed to fetch fresh profile:', e);
      }

      // Extract role, ID, region, and approval date from profile
      const reporterRole = latestProfile?.pressRole?.trim() || latestProfile?.role?.trim() || consentFormData.role?.trim() || 'CORRESPONDENT';
      const reporterId = latestProfile?.reporterId?.trim() || latestProfile?.userId?.trim() || null;
      const reporterRegion = latestProfile?.region?.trim() || null;
      const approvedAt = latestProfile?.approvedAt || null;
      
      console.log('[Letter] Using role:', reporterRole, 'ID:', reporterId, 'Region:', reporterRegion, 'ApprovedAt:', approvedAt, 'Profile:', latestProfile);
      
      const result = await generateConsentLetterPDF(consentFormData, resolvedName, reporterRole, reporterId, reporterRegion, approvedAt);
      if (result.success) {
        setLetterDownloadSuccess({
          uniqueId: result.uniqueId,
          validFrom: result.validFrom,
          validUpto: result.validUpto,
        });
      }
    } catch (error) {
      alert('पत्र डाउनलोड विफल: ' + error.message);
    } finally {
      setGeneratingLetterPDF(false);
    }
  };

  const openMyCard = async () => {
    try {
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      const res = await api.get('/api/users/me', { headers });
      if (res && res.data && res.data.success) {
        const profile = res.data.data;
        setMyProfile(profile);
        
        // Check if consent form is already filled
        if (profile.isConsent) {
          // Show card directly
          if ((!profile.pressRole || !profile.pressRole.trim()) && profile._id) {
            try {
              const cardResp = await api.get(`/api/users/reporters/${profile._id}/card`);
              if (cardResp && cardResp.data && cardResp.data.data && cardResp.data.data.roleLabel) {
                profile.pressRole = cardResp.data.data.roleLabel;
              }
            } catch (e) {
              // ignore
            }
          }
          setMyProfile(profile);
          (async function() {
            try {
              const raw = (profile.avatar && (profile.avatar.startsWith('http') ? profile.avatar : (process.env.REACT_APP_API_BASE || '') + profile.avatar)) || 'https://via.placeholder.com/120x150.png?text=Photo';
              const dataUrl = await fetchImageAsDataURL(raw);
              setCardPhotoDataUrl(dataUrl);
            } catch (e) {
              console.warn('Failed to fetch avatar as data URL', e && e.message);
              setCardPhotoDataUrl(null);
            }
          })();
          setShowCard(true);
        } else {
          // Show consent form first
          setShowConsentForm(true);
        }
      } else {
        alert((res && res.data && res.data.message) || 'Failed to load profile');
      }
    } catch (err) {
      alert(err.response && err.response.data && err.response.data.message ? err.response.data.message : (err.message || 'Failed'));
    }
  };

  // Utility: fetch image URL and convert to base64 data URL (or return null on failure)
  async function fetchImageAsDataURL(url) {
    try {
      // Use fetch to get the resource; allow CORS mode (will fail if server blocks)
      const resp = await fetch(url, { mode: 'cors' });
      if (!resp.ok) throw new Error('Failed to fetch image: ' + resp.status);
      const blob = await resp.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.warn('fetchImageAsDataURL direct fetch failed for', url, err && err.message);
      // Try backend proxy as a fallback to avoid CORS issues
      try {
        const proxyUrl = `${process.env.REACT_APP_API_BASE || ''}/api/images/proxy?url=${encodeURIComponent(url)}`;
        const presp = await fetch(proxyUrl, { mode: 'cors' });
        if (!presp.ok) throw new Error('Proxy fetch failed: ' + presp.status);
        const pblob = await presp.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(new Error('FileReader error'));
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(pblob);
        });
      } catch (e2) {
        console.warn('fetchImageAsDataURL proxy fallback failed for', url, e2 && e2.message);
        return null;
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const form = new FormData();
      form.append('title', title);
      form.append('description', description);
      form.append('content', content);
      form.append('author', author);
      form.append('location', location);
      form.append('type', type);
      console.log('Submit start', { image: image ? { name: image.name, size: image.size, type: image.type } : null, video: video ? { name: video.name, size: video.size, type: video.type } : null, galleryCount: galleryFiles ? galleryFiles.length : 0, type });
      if (image) form.append('image', image);
      if (video) form.append('video', video);
      if (type === 'gallery' && galleryFiles && galleryFiles.length) {
        for (let i = 0; i < galleryFiles.length; i++) {
          form.append('galleryImages', galleryFiles[i]);
        }
      }
      if (type === 'event') {
        if (eventDate) form.append('eventDate', eventDate);
        if (eventVenue) form.append('eventVenue', eventVenue);
      }

      let res;
      if (editState && editNews && editNews._id) {
        // Update existing
        res = await api.put(`/api/news/${editNews._id}`, form, { headers: token ? { 'Authorization': 'Bearer ' + token } : {} });
      } else {
        // Create new
        // choose endpoint based on role: reporters should use reporter/upload
        const endpoint = (storedRole === 'reporter') ? '/api/news/reporter/upload' : '/api/news/admin/upload';
        res = await api.post(endpoint, form, { headers: token ? { 'Authorization': 'Bearer ' + token } : {} });
      }
      const data = res && res.data ? res.data : null;
      if (res && res.status && (res.status === 401 || res.status === 403)) {
        alert((data && data.message) || 'Unauthorized');
        // clear local auth and redirect to admin login
        localStorage.removeItem('token');
        localStorage.removeItem('role');
        return navigate('/admin/login');
      }
      if (data && data.success) {
        alert(editState ? 'Updated successfully' : 'Uploaded and pending approval');
        // clear form and navigate back to approvals if editing
        if (editState) {
          navigate('/superadmin/approval');
          return;
        }
        setTitle(''); setDescription(''); setContent(''); setAuthor(''); setLocation(''); setImage(null); setFileName('No file chosen');
        setVideo(null); setVideoFileName('No file chosen'); setVideoPreviewUrl(null);
        setGalleryFiles([]); setGalleryPreviewUrls([]);
        setEventDate(''); setEventVenue(''); setType('normal');
      } else {
        alert(data.message || 'Upload failed');
      }
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) {
      // revoke previous object URL if any
      if (previewIsObject.current && previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch(e){}
      }
      setImage(f);
      setFileName(f.name);
      try{
        const obj = URL.createObjectURL(f);
        setPreviewUrl(obj);
        previewIsObject.current = true;
      }catch(e){ setPreviewUrl(null); previewIsObject.current = false; }
    } else {
      setImage(null);
      setFileName('No file chosen');
      if (previewIsObject.current && previewUrl) { try{ URL.revokeObjectURL(previewUrl); }catch(e){} }
      setPreviewUrl(null);
      previewIsObject.current = false;
    }
  };

  const handleVideoChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) {
      // revoke previous object URL if any
      if (videoPreviewIsObject.current && videoPreviewUrl) {
        try { URL.revokeObjectURL(videoPreviewUrl); } catch(e){}
      }
      setVideo(f);
      setVideoFileName(f.name);
      try{
        const obj = URL.createObjectURL(f);
        setVideoPreviewUrl(obj);
        videoPreviewIsObject.current = true;
      }catch(e){ setVideoPreviewUrl(null); videoPreviewIsObject.current = false; }
    } else {
      setVideo(null);
      setVideoFileName('No file chosen');
      if (videoPreviewIsObject.current && videoPreviewUrl) { try{ URL.revokeObjectURL(videoPreviewUrl); }catch(e){} }
      setVideoPreviewUrl(null);
      videoPreviewIsObject.current = false;
    }
  };

  return (
    <div className="container admin-upload">
      <div className="header-row">
        {/* <div className="page-label">Admin Dashboard</div> */}
        {consentFormData && (
          <button 
            className="download-letter-btn" 
            onClick={handleDownloadLetter}
            disabled={generatingLetterPDF}
          >
            {generatingLetterPDF ? 'पत्र डाउनलोड हो रहा है...' : '📄 नियुक्ति पत्र डाउनलोड करें'}
          </button>
        )}
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>
      <div className="upload-card">
        <div className="card-title">
          <h2>Upload RR News</h2>
          <div className="sub">Fill details and attach a clear image</div>
        </div>
        <div className="upload-type-row" style={{display:'flex',gap:8,marginBottom:12}}>
          <label style={{alignSelf:'center',marginRight:6}}>Type:</label>
          <button type="button" className={`type-btn ${type==='normal'?'active':''}`} onClick={()=>setType('normal')}>Normal</button>
          <button type="button" className={`type-btn ${type==='gallery'?'active':''}`} onClick={()=>setType('gallery')}>Gallery</button>
          <button type="button" className={`type-btn ${type==='event'?'active':''}`} onClick={()=>setType('event')}>Event</button>
        </div>
        {/* Reporter: quick access to ID card if approved */}
        {storedRole === 'reporter' && (
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
            <button type="button" className="ghost my-card pulse" onClick={openMyCard}>My Card</button>
          </div>
        )}
        {type === 'gallery' && (
          <div className="form-group custom-file">
            <label>Gallery Images (multiple)</label>
            <input id="galleryInput" type="file" accept="image/*" multiple onChange={(e)=>{
              const files = Array.from(e.target.files || []);
              setGalleryFiles(files);
              // create previews
              const urls = files.map(f => {
                try { return URL.createObjectURL(f); } catch(e){ return null; }
              }).filter(Boolean);
              setGalleryPreviewUrls(urls);
            }} />
            <label htmlFor="galleryInput" className={`file-label ${galleryFiles.length? 'selected' : ''}`}>Choose Images</label>
            <span className="file-name">{galleryFiles.length ? `${galleryFiles.length} files` : 'No files chosen'}</span>
            <div className="gallery-preview" style={{display:'flex',gap:8,marginTop:8}}>
              {galleryPreviewUrls.length ? galleryPreviewUrls.map((u,i)=> <img key={i} src={u} alt={`g${i}`} style={{width:80,height:60,objectFit:'cover',borderRadius:6}} />) : <span style={{opacity:0.6}}>No gallery previews</span>}
            </div>
          </div>
        )}
        {showCard && myProfile && (
          <div style={{position:'fixed',left:0,top:0,right:0,bottom:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.4)',zIndex:9999}}>
            <div className="admin-card-root" style={{background:'#fff',padding:16,borderRadius:8,maxWidth:920,width:'95%',overflow:'auto',maxHeight:'90vh'}} ref={cardRef}>
              <div style={{display:'flex',justifyContent:'flex-end'}}>
                <button onClick={() => setShowCard(false)} style={{border:'none',background:'transparent',fontSize:18}}>✕</button>
              </div>
              <div className="admin-card-row" style={{display:'flex',gap:12,justifyContent:'center',alignItems:'flex-start'}}>
                <div className="admincard-col" style={{width:380}}>
                  <PressCard
                    logoUrl={logo}
                    heading="RR NEWS TV FASTEST UPDATE"
                    id={myProfile.reporterId || ''}
                    valid={(function(){ const base = myProfile.approvedAt || myProfile.createdAt || new Date(); const d=new Date(base); d.setFullYear(d.getFullYear()+1); return d.toLocaleDateString('en-GB'); })()}
                    name={myProfile.name}
                    role={myProfile.pressRole && myProfile.pressRole.trim() ? myProfile.pressRole : (myProfile.role === 'reporter' ? 'Reporter' : myProfile.role)}
                    region={myProfile.region || ''}
                    photo={cardPhotoDataUrl || (myProfile.avatar && (myProfile.avatar.startsWith('http') ? myProfile.avatar : (process.env.REACT_APP_API_BASE || '') + myProfile.avatar)) || 'https://via.placeholder.com/120x150.png?text=Photo'}
                  />
                </div>
                <div className="admincard-col" style={{width:380}}>
                  <IDCardBack data={{ logo, dob: myProfile.dob || '', bloodGroup: myProfile.bloodGroup || '', email: myProfile.email || '', address: myProfile.address || '' }} />
                </div>
              </div>

              <div className="press-modal-actions" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <button 
                  onClick={async ()=>{
                    try{
                      const el = cardRef.current.querySelector('.press-card');
                      if (!el) throw new Error('Front not found');
                      applyCaptureStyles();
                      const canvas = await html2canvas(el, {scale:2, useCORS:true});
                      removeCaptureStyles();
                      const data = canvas.toDataURL('image/png');
                      const a = document.createElement('a'); a.href = data; a.download = `${myProfile.reporterId||myProfile.name}-front.png`; document.body.appendChild(a); a.click(); a.remove();
                    }catch(e){ removeCaptureStyles(); alert('Failed to generate PNG: '+(e && e.message)); }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: "600",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#ffffff",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease",
                  }}
                >
                  Download Front PNG
                </button>

                <button 
                  onClick={async ()=>{
                    try{
                      const el = cardRef.current.querySelector('.idcard-frame') || cardRef.current.querySelector('.idcard-container');
                      if (!el) throw new Error('Back not found');
                      applyCaptureStyles();
                      const canvas = await html2canvas(el, {scale:2, useCORS:true});
                      removeCaptureStyles();
                      const data = canvas.toDataURL('image/png');
                      const a = document.createElement('a'); a.href = data; a.download = `${myProfile.reporterId||myProfile.name}-back.png`; document.body.appendChild(a); a.click(); a.remove();
                    }catch(e){ removeCaptureStyles(); alert('Failed to generate PNG: '+(e && e.message)); }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: "600",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#ffffff",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease",
                  }}
                >
                  Download Back PNG
                </button>

                <button 
                  onClick={async ()=>{
                  try{
                    const frontEl = cardRef.current.querySelector('.press-card');
                    if (!frontEl) throw new Error('Front not found');
                    applyCaptureStyles();
                    const frontCanvas = await html2canvas(frontEl, {scale:2, useCORS:true});
                    const frontData = frontCanvas.toDataURL('image/png');
                    const pdf = new jsPDF({ unit: 'px', format: [frontCanvas.width, frontCanvas.height] });
                    pdf.addImage(frontData, 'PNG', 0, 0, frontCanvas.width, frontCanvas.height);
                    const backEl = cardRef.current.querySelector('.idcard-frame') || cardRef.current.querySelector('.idcard-container');
                    if (backEl) {
                      const backCanvas = await html2canvas(backEl, {scale:2, useCORS:true});
                      const backData = backCanvas.toDataURL('image/png');
                      pdf.addPage([backCanvas.width, backCanvas.height], 'portrait');
                      pdf.addImage(backData, 'PNG', 0, 0, backCanvas.width, backCanvas.height);
                    }
                    pdf.save(`${myProfile.reporterId||myProfile.name}-card.pdf`);
                    removeCaptureStyles();
                  }catch(e){ removeCaptureStyles(); alert('Failed to generate PDF: '+(e && e.message)); }
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: "600",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#ffffff",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    transition: "all 0.3s ease",
                  }}
                >
                  Download PDF
                </button>
              
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="upload-form">
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input id="title" className="fancy-input" value={title} onChange={e=>setTitle(e.target.value)} placeholder="Enter headline" required />
          </div>

          <div className="row">
            <div className="col form-group">
              <label htmlFor="author">Author</label>
              <input id="author" className="fancy-input" value={author} onChange={e=>setAuthor(e.target.value)} placeholder="Author name" />
            </div>
            <div className="col form-group">
              <label htmlFor="location">Location</label>
              <input id="location" className="fancy-input" value={location} onChange={e=>setLocation(e.target.value)} placeholder="Location (city)" />
            </div>
          </div>

          {type === 'event' && (
            <div className="row">
              <div className="col form-group">
                <label htmlFor="eventDate">Event Date & Time</label>
                <input id="eventDate" type="datetime-local" className="fancy-input" value={eventDate} onChange={e=>setEventDate(e.target.value)} />
              </div>
              <div className="col form-group">
                <label htmlFor="eventVenue">Event Venue</label>
                <input id="eventVenue" className="fancy-input" value={eventVenue} onChange={e=>setEventVenue(e.target.value)} placeholder="Venue / Address" />
              </div>
            </div>
          )}

          {type !== 'gallery' && (
            <div className="form-group custom-file">
              <label>Image</label>
              <input id="imageInput" type="file" accept="image/*" onChange={handleFileChange} />
              <label htmlFor="imageInput" className={`file-label ${fileName !== 'No file chosen' ? 'selected' : ''}`}>Choose Image</label>
              <span className="file-name">{fileName}</span>
              <div className="file-preview" style={{marginLeft:8}}>
                {/* If video uploaded, show video/audio preview in the image area; otherwise show image preview */}
                {videoPreviewUrl ? (
                  // try video first; audio will render via <audio>
                  (videoPreviewUrl && (video && video.type && video.type.startsWith('audio/'))) ? (
                    <audio src={videoPreviewUrl} controls style={{width:'100%'}} />
                  ) : (
                    <video src={videoPreviewUrl} controls playsInline style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:8}} onError={() => {
                      // preview failed to play in-browser (codec/metadata). Keep the file selected but clear the preview URL so image fallback shows.
                      try { if (videoPreviewIsObject.current && videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl); } catch(e){}
                      videoPreviewIsObject.current = false;
                      setVideoPreviewUrl(null);
                      // user may still upload; inform them
                      setTimeout(()=>alert('Preview for this media is not supported in your browser. It may still upload and play for users after approval.'), 50);
                    }} />
                  )
                ) : (previewUrl ? <img src={previewUrl} alt="preview" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:8}} /> : <span>Preview</span>)}
              </div>
            </div>
          )}

          {type !== 'gallery' && (
            <div className="form-group custom-file">
              <label>Video / Audio (optional)</label>
              <input id="videoInput" type="file" accept="video/*,audio/*" onChange={handleVideoChange} />
              <label htmlFor="videoInput" className={`file-label ${videoFileName !== 'No file chosen' ? 'selected' : ''}`}>Choose Media</label>
              <span className="file-name">{videoFileName}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="summary">Summary</label>
            <textarea id="summary" className="fancy-input" value={description} onChange={e=>setDescription(e.target.value)} placeholder="Short summary" required />
          </div>

          <div className="form-group">
            <label htmlFor="details">Details</label>
            <textarea id="details" className="fancy-input" value={content} onChange={e=>setContent(e.target.value)} placeholder="Full details" required />
          </div>

          <div className="upload-actions">
            <button type="button" className="ghost" onClick={() => { setTitle(''); setDescription(''); setContent(''); setAuthor(''); setLocation(''); setImage(null); setFileName('No file chosen'); }}>Reset</button>
            <button type="submit" className="primary" disabled={submitting}>{submitting ? 'Uploading...' : 'Upload'}</button>
          </div>
        </form>

        {storedRole !== 'reporter' && <AdminVideoManager token={token} />}
      </div>
      
      {submitting && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div className="loading">
            <div className="spinner"></div>
          </div>
        </div>
      )}
      
      {showConsentForm && myProfile && (
        <ConsentForm 
          onClose={() => setShowConsentForm(false)} 
          onFormDataSaved={(formData) => setConsentFormData(formData)}
          onSuccess={async () => {
            // After consent submission, fetch updated profile and show card
            try {
              const headers = token ? { Authorization: 'Bearer ' + token } : {};
              const res = await api.get('/api/users/me', { headers });
              if (res && res.data && res.data.success) {
                const profile = res.data.data;
                if ((!profile.pressRole || !profile.pressRole.trim()) && profile._id) {
                  try {
                    const cardResp = await api.get(`/api/users/reporters/${profile._id}/card`);
                    if (cardResp && cardResp.data && cardResp.data.data && cardResp.data.data.roleLabel) {
                      profile.pressRole = cardResp.data.data.roleLabel;
                    }
                  } catch (e) {}
                }
                setMyProfile(profile);
                (async function() {
                  try {
                    const raw = (profile.avatar && (profile.avatar.startsWith('http') ? profile.avatar : (process.env.REACT_APP_API_BASE || '') + profile.avatar)) || 'https://via.placeholder.com/120x150.png?text=Photo';
                    const dataUrl = await fetchImageAsDataURL(raw);
                    setCardPhotoDataUrl(dataUrl);
                  } catch (e) {
                    console.warn('Failed to fetch avatar as data URL', e && e.message);
                    setCardPhotoDataUrl(null);
                  }
                })();
                setShowCard(true);
              }
            } catch (err) {
              console.warn('Failed to load updated profile', err);
            }
          }}
          userProfile={myProfile}
        />
      )}

      {/* Appointment Letter Download Success Modal */}
      {letterDownloadSuccess && (
        <div className="letter-success-modal-overlay">
          <div className="letter-success-modal">
            <div className="letter-success-header">
              <span className="letter-success-icon">✓</span>
            </div>
            <h2 className="letter-success-title">नियुक्ति पत्र सफलतापूर्वक डाउनलोड किया गया!</h2>
            <div className="letter-success-content">
              <div className="letter-success-item">
                <span className="letter-label">नियुक्ति ID:</span>
                <span className="letter-value">{letterDownloadSuccess.uniqueId}</span>
              </div>
              <div className="letter-success-item">
                <span className="letter-label">वैध से:</span>
                <span className="letter-value">{letterDownloadSuccess.validFrom}</span>
              </div>
              <div className="letter-success-item">
                <span className="letter-label">वैध तक:</span>
                <span className="letter-value">{letterDownloadSuccess.validUpto}</span>
              </div>
            </div>
            <button
              className="letter-success-btn"
              onClick={() => setLetterDownloadSuccess(null)}
            >
              बंद करें
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUpload;


